import { Component, OnInit, EventEmitter, Output, Inject, ViewChild, ElementRef } from "@angular/core";
import { DOCUMENT } from "@angular/common";
import { ICellRendererAngularComp } from "ag-grid-angular";
import { TranslateService } from "@ngx-translate/core";
import { BreezeButtonComponent } from "src/app/components/breeze-button/breeze-button.component";

@Component({
  standalone: false,
  selector: "wfm-esp-grid-item-action",
  templateUrl: "./grid-item-action.component.html",
  styleUrls: ["./grid-item-action.component.scss"]
})
export class GridItemActionComponent implements OnInit, ICellRendererAngularComp {

  public currentItem: any;
  private params: any;
  private callbackMethod: any;
  private messageDelete: string;

  // ESP-18470/related a11y (WCAG 2.2 SC 4.1.2 Name, Role, Value):
  // The delete icon is a control rendered once per row. Without an
  // accessible name that disambiguates the row it acts on, a screen
  // reader user navigating multiple rows hears "Delete button" repeated
  // with no context. The aria-label is computed from the row's data
  // using either an explicit getter callback or a configured field name.
  itemName = "";
  ariaLabel = "";

  // ESP-21814 (WCAG 2.1.1 Keyboard): whether the delete button is a real Tab
  // stop. It defaults to -1 because the grids this renderer was written for
  // (Manage Plans, Imported Forecasts) run a cell-as-Tab-stop model: their
  // `suppressGridTabToInteractiveControls` /
  // `tabSuppressKeyboardEventForFocusableChildren` suppressors make the .ag-cell
  // the single stop and focus this button programmatically, so a tabindex of 0
  // would give those grids a redundant second stop on the same visual box.
  //
  // The New Hire grid is the opposite: it uses
  // `enableGridInteractiveControlTabOrder`, whose Tab walk skips .ag-cell
  // wrappers AND anything with a negative tabindex - so with -1 neither the cell
  // nor the button was ever reachable and the delete action had no keyboard route
  // at all. That grid opts in with `tabIndex: 0` in its cellRendererParams.
  deleteTabIndex = -1;

  // ESP-21814 (WCAG 4.1.2 Name, Role, Value): this renderer runs once per row, so
  // a literal id would be emitted many times in one grid. Duplicate ids make the
  // confirmation dialog's `aria-labelledby` resolve to the FIRST match, i.e. a
  // destructive prompt announced against the wrong row. Suffix both ids per
  // instance.
  private static instanceSeq = 0;
  readonly instanceId = `delete-item-${++GridItemActionComponent.instanceSeq}`;
  readonly titleId = `${this.instanceId}-title`;

  @ViewChild("deleteButton") deleteButton: ElementRef<HTMLButtonElement>;
  @ViewChild("cancelBtn") cancelBtn: BreezeButtonComponent;
  @ViewChild("confirmBtn") confirmBtn: BreezeButtonComponent;

  @Output()
  deleteClicked: EventEmitter<any> = new EventEmitter<any>();

  constructor(private translate: TranslateService, @Inject(DOCUMENT) private document: Document) { }

  ngOnInit() {
  }

  agInit(params: any) {
    this.currentItem = params.data;
    this.params = params;
    this.callbackMethod = params.callback;
    this.messageDelete = params.messageDelete;
    this.itemName = this.resolveItemName(params);
    this.ariaLabel = this.buildAriaLabel(params, this.itemName);
    // Explicit opt-in only: any other value keeps the historical -1.
    this.deleteTabIndex = params?.tabIndex === 0 ? 0 : -1;
  }

  private resolveItemName(params: any): string {
    if (!params) {
      return "";
    }
    if (typeof params.getItemName === "function") {
      try {
        const value = params.getItemName(params.data);
        return value == null ? "" : String(value).trim();
      } catch {
        return "";
      }
    }
    if (params.itemNameField && params.data) {
      const value = params.data[params.itemNameField];
      return value == null ? "" : String(value).trim();
    }
    // Fall back to well-known row-name fields so the delete control still gets a
    // disambiguating accessible name when no explicit getter/field is configured.
    if (params.data) {
      for (const field of ["planName", "forecastName", "managementUnit", "name"]) {
        const value = params.data[field];
        if (value != null && String(value).trim() !== "") {
          return String(value).trim();
        }
      }
    }
    return "";
  }

  private buildAriaLabel(params: any, itemName: string): string {
    const labelKey = (params && params.actionLabelKey) || "icon.delete.tooltip";
    // TranslateService.instant() returns the key itself when a translation is
    // missing, so a falsy check isn't enough — also fall back when the result
    // equals the key (avoids leaking "icon.delete.tooltip" into the aria-label).
    const translated = this.translate.instant(labelKey);
    const action = (translated && translated !== labelKey) ? translated : "Delete";
    return itemName ? `${action} ${itemName}` : action;
  }

  emitData() {
    this.deleteClicked.emit(this.currentItem);
  }

  confirm(){
    this.callbackMethod(this.currentItem);
  }

  focusPopoverDialog() {
    // a11y (WCAG 2.4.3 Focus Order, ARIA dialog pattern): place initial focus
    // on the first interactive control (Cancel) so keyboard users can
    // immediately activate or Tab to Confirm. Falls back to the dialog wrapper.
    setTimeout(() => {
      const cancelEl = this.cancelBtn?.button?.nativeElement as HTMLElement | undefined;
      if (cancelEl) {
        cancelEl.focus();
        return;
      }
      const dialog = this.document.querySelector('ngb-popover-window [role="dialog"]') as HTMLElement;
      dialog?.focus();
    });
  }

  // a11y (focus trap): cycle Tab / Shift+Tab between Cancel and Confirm so
  // focus does not escape the open dialog into the rest of the page.
  onDialogTab(event: KeyboardEvent, shift: boolean): void {
    const cancelEl = this.cancelBtn?.button?.nativeElement as HTMLElement | undefined;
    const confirmEl = this.confirmBtn?.button?.nativeElement as HTMLElement | undefined;
    if (!cancelEl || !confirmEl) return;
    const active = this.document.activeElement;
    if (!shift && active === confirmEl) {
      event.preventDefault();
      cancelEl.focus();
    } else if (shift && active === cancelEl) {
      event.preventDefault();
      confirmEl.focus();
    }
  }

  // a11y: when the popover closes (Escape, Cancel, Confirm, click outside),
  // return focus to the AG Grid cell via the Grid API. Using setFocusedCell()
  // ensures AG Grid's own focus management wins over ngbPopover's internal
  // focus-return logic (which would otherwise put focus back on the <i> icon
  // — but the icon is tabindex="-1" and not a desired focus target).
  returnFocusToCell(): void {
    setTimeout(() => {
      const api = this.params?.api;
      if (!api) {
        return;
      }
      // Resolve the row index from the LIVE row node rather than the cached
      // params.rowIndex: AG Grid captures rowIndex once when the cell renderer
      // is created and does NOT keep that snapshot in sync when rows are
      // re-sorted / re-rendered, so relying on it restored focus (and the cell
      // focus border) to the wrong — typically the last — row after the delete
      // confirmation closed. node.rowIndex always reflects the current position.
      const rowIndex = this.params?.node?.rowIndex ?? this.params?.rowIndex;
      if (rowIndex == null || rowIndex < 0) {
        return;
      }
      api.setFocusedCell(rowIndex, this.params.column.getId());
      // ESP-21814 (WCAG 2.4.3 Focus Order): a dialog must return focus to the
      // control that opened it. `setFocusedCell` forces browser focus onto the
      // .ag-cell WRAPPER, which is the right place when the cell is the grid's
      // Tab stop - but when this button is itself the Tab stop, focus has to come
      // back to the button, or Tab would resume from the wrapper instead.
      if (this.deleteTabIndex === 0) {
        this.deleteButton?.nativeElement?.focus();
      }
    });
  }

  refresh(): boolean {
    return false;
  }
}
